/**
 * Glossary — pełna lista skrótów branżowych MASKIL (2026-05-26).
 *
 * Strona dla nowych userów: "co znaczy SWZ, RFQ, BoQ, JEDZ, DALI…".
 * Linkowana z sidebar (sekcja Pomoc) + ikoną Info w nagłówkach modułów.
 */

import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { BookOpen, Search } from 'lucide-react';
import { allTerms } from '../lib/glossary';

const CATEGORIES = {
  procurement:    { label: 'Zamówienia publiczne (PZP)', terms: ['SWZ','PZP','JEDZ','CPV','BZP','TED','PIN'] },
  profitroom:     { label: 'Profit Room / RFQ',          terms: ['RFQ','BoQ'] },
  investments:    { label: 'Inwestycje (Pipeline B)',    terms: ['GUNB','WPF','DŚU','RDOŚ','GDOŚ'] },
  intel:          { label: 'Wywiad / dane',              terms: ['SPHINX','KRS','CEIDG','BIP'] },
  lighting:       { label: 'Oświetlenie / LED',          terms: ['LED','DALI','DIALux','UGR','IP'] },
};

function termCategory(term) {
  for (const [k, c] of Object.entries(CATEGORIES)) {
    if (c.terms.includes(term)) return k;
  }
  return 'other';
}

export default function Glossary() {
  const [q, setQ] = useState('');
  const terms = useMemo(() => allTerms(), []);
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return terms;
    return terms.filter(t =>
      t.term.toLowerCase().includes(qq) ||
      t.full.toLowerCase().includes(qq) ||
      t.desc.toLowerCase().includes(qq)
    );
  }, [terms, q]);

  // Group by category
  const byCategory = useMemo(() => {
    const out = {};
    for (const t of filtered) {
      const cat = termCategory(t.term);
      if (!out[cat]) out[cat] = [];
      out[cat].push(t);
    }
    return out;
  }, [filtered]);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <BookOpen className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Słownik skrótów</h1>
          <p className="text-sm text-muted-foreground">
            Wszystkie skróty branżowe używane w MASKIL — kliknij na termin po pełną definicję i kontekst.
          </p>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Wyszukaj termin (np. SWZ, RFQ, oświetlenie...)"
          className="pl-8"
          data-testid="glossary-search"
        />
      </div>

      {Object.keys(byCategory).length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Brak terminów pasujących do „{q}".
          </CardContent>
        </Card>
      ) : (
        Object.entries(CATEGORIES).map(([key, cat]) => {
          const items = byCategory[key];
          if (!items || items.length === 0) return null;
          return (
            <div key={key}>
              <div className="text-xs uppercase tracking-wider text-muted-foreground/70 mb-2 px-1">
                {cat.label}
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                {items.map((t) => (
                  <Card key={t.term}>
                    <CardContent className="p-3 space-y-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <Badge variant="outline" className="font-mono text-sm">{t.term}</Badge>
                        <span className="text-sm font-medium">{t.full}</span>
                      </div>
                      <div className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
                        {t.desc}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })
      )}

      <Card className="bg-muted/30 mt-6">
        <CardContent className="p-3 text-xs text-muted-foreground">
          💡 Wskazówka: w wielu miejscach MASKIL skróty mają tooltipy na hover —
          najedź kursorem na podkreśloną kropkowanie linią nazwę żeby zobaczyć
          krótką definicję bez wychodzenia z bieżącego widoku.
        </CardContent>
      </Card>
    </div>
  );
}
